/**
==============================================================================================
++++++++++++++++++++++++ UNIT CONVERSION REFERENCE +++++++++++++++++++++++++++
0.1 nm = 0.0001µm
Use this reference to convert pixel-based distances
==============================================================================================
*/
import layoutEngine from "../libraries/layoutEngine";
import ProjectManager from "./ProjectManager";
export default class RulerToolHandler {
  constructor(engine) {
    this.engine = engine;
    this.isDrawingRuler = false;
    this.rulerStart = null;
    this.rulersByCell = {}; // {[cellName]: rulerArray}
    this.redoStack = [];
    this._redoCell = null; // Tracks which cell the redoStack belongs to
    this.rulerLineColor = "white";
    this.rulerUnit = 10;
    this.gridSize = 10;
    this.unitScaleFactor = 1;
    this.rulerValues = 2;
    this.rulerUnitSymbol = "nm";
    this.rulerLineMode = null || "orthogonal";
    this.tempRuler = null;
    this.fontSize = null || 1;
    this.startMarker = null;
    this.rulerOnGrid = true;
    this.polylineMode = false;
    this.UNIT_CONVERSIONS = {
      nm: 0.1,
      µm: 0.0001,
    };
    this.UNIT_PRECISION = {
      nm: 1,
      µm: 4,
    };

    // Polyline-specific properties
    this.currentPolylinePoints = [];
    this.isDrawingPolyline = false;
    this.tempPolylineSegment = null;
  }
  // get currrent cell to draw ruler
  getCurrentCell() {
    const Cellname = ProjectManager?.getSelectedCell?.();
    if (Cellname) return Cellname;
    const fallbackCellname = layoutEngine?.getActiveCellName?.();
    return fallbackCellname ?? null;
  }
  // Active cell's ruler bucket
  getActiveBuckets() {
    const cell = this.getCurrentCell();
    // If no active cell, avoid creating a bucket under a 'null' key
    if (!cell) {
      if (this._redoCell !== null) {
        this.redoStack.length = 0;
        this._redoCell = null;
      }
      return { list: [], redo: this.redoStack };
    }
    if (!this.rulersByCell[cell]) this.rulersByCell[cell] = [];
    if (this._redoCell !== cell) {
      this.redoStack.length = 0;
      this._redoCell = cell;
    }
    return { list: this.rulersByCell[cell], redo: this.redoStack };
  }
  // change cursor to cross-hair logic
  setCursorRuler() {
    if (window.ToolManager.getCurrentTool() === "ruler") {
      this.engine.canvas.style.cursor = "crosshair";
    }
  }
  // ticks no. logic
  setRulerValues(count) {
    this.rulerValues = count;
  }
  // for unit change logic
  setRulerUnit(unitValue, unitSymbol) {
    this.rulerUnit = unitValue;
    this.rulerUnitSymbol = unitSymbol;
  }
  // color changer logic
  setRulerLineColor(color) {
    this.rulerLineColor = color;
  }
  // gird size logic according to spacing
  snapToGrid(value) {
    const spacing = 1;
    return Math.round(value / spacing) * spacing;
  }
  // converting logic for coordiantes to nearest point
  snapPointToGrid(point) {
    return {
      x: this.snapToGrid(point.x),
      y: this.snapToGrid(point.y),
    };
  }
  // getting real og coordinates logic
  getWorldCoords(e) {
    const rect = this.engine.canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left - this.engine.offsetX) / this.engine.scale;
    const y = (e.clientY - rect.top - this.engine.offsetY) / this.engine.scale;
    return { x, y };
  }

  // Enhanced double-click handler for polyline mode
  handleMouseRulerDBclick() {
    // console.log("Double click executed");
    if (this.polylineMode && this.isDrawingPolyline) {
      this.finishPolyline();
    }
  }

  // Enhanced mouse down handler with polyline support
  handleRulerMouseDown(e) {
    // Ensure a cell is selected before drawing
    const cell = this.getCurrentCell();
    if (!cell) {
      return alert("Please select or add cell before drawing");
    }

    const { x, y } = this.getWorldCoords(e);

    if (this.polylineMode) {
      return this.handlePolylineMouseDown(x, y);
    } else {
      return this.handleStandardRulerMouseDown(x, y);
    }
  }

  // Standard two-point ruler logic (existing)
  handleStandardRulerMouseDown(x, y) {
    // First click: start point
    if (!this.isDrawingRuler) {
      let startX = x;
      let startY = y;

      if (this.rulerOnGrid) {
        const snappedStart = this.snapPointToGrid({ x, y });
        startX = snappedStart.x;
        startY = snappedStart.y;
      }

      this.rulerStart = { x: startX, y: startY };
      this.startMarker = { x: startX, y: startY };
      this.isDrawingRuler = true;
      return;
    }

    // Second click: end point
    let endX = x;
    let endY = y;

    if (this.rulerOnGrid) {
      const snappedEnd = this.snapPointToGrid({ x, y });
      endX = snappedEnd.x;
      endY = snappedEnd.y;
    }

    // Apply angle snapping
    const { snappedX, snappedY } = this.applyAngleConstraints(
      this.rulerStart.x,
      this.rulerStart.y,
      endX,
      endY
    );

    const { list, redo } = this.getActiveBuckets();
    // Creating a new ruler clears the redo stack for the active cell
    if (redo && Array.isArray(redo)) redo.length = 0;
    list.push({
      id: list.length,
      start: this.rulerStart,
      end: { x: snappedX, y: snappedY },
      offset: { x: 0, y: 0 },
      unit: this.rulerUnitSymbol,
      mode: this.rulerLineMode,
      polylineMode: false,
    });

    this.resetStandardRuler();
  }

  // New polyline mouse down handler
  handlePolylineMouseDown(x, y) {
    let pointX = x;
    let pointY = y;

    if (this.rulerOnGrid) {
      const snappedPoint = this.snapPointToGrid({ x, y });
      pointX = snappedPoint.x;
      pointY = snappedPoint.y;
    }

    const newPoint = { x: pointX, y: pointY };

    if (!this.isDrawingPolyline) {
      // Start new polyline
      this.startPolyline(newPoint);
    } else {
      // Add point to existing polyline
      this.addPointToPolyline(newPoint);
    }
  }
  // taking a start coordiantes
  startPolyline(startPoint) {
    this.currentPolylinePoints = [startPoint];
    this.isDrawingPolyline = true;
    this.startMarker = startPoint;
  }
  //add  next point to array
  addPointToPolyline(newPoint) {
    const lastPoint =
      this.currentPolylinePoints[this.currentPolylinePoints.length - 1];

    // Apply angle constraints between last point and new point
    const { snappedX, snappedY } = this.applyAngleConstraints(
      lastPoint.x,
      lastPoint.y,
      newPoint.x,
      newPoint.y
    );

    const constrainedPoint = { x: snappedX, y: snappedY };
    this.currentPolylinePoints.push(constrainedPoint);

    // The ruler will be created only when user double-clicks to finish
    this.tempPolylineSegment = null;
  }
  //on finish of polyline mode s
  finishPolyline() {
    if (!this.isDrawingPolyline || this.currentPolylinePoints.length < 2) {
      this.resetPolyline();
      return;
    }

    // Now create the single ruler with all points
    const { list, redo } = this.getActiveBuckets();
    if (redo && Array.isArray(redo)) redo.length = 0;
    list.push({
      id: list.length,
      points: [...this.currentPolylinePoints.slice(0, -1)],
      offset: { x: 0, y: 0 },
      unit: this.rulerUnitSymbol,
      mode: this.rulerLineMode,
      polylineMode: this.polylineMode,
    });

    this.resetPolyline();
  }
  //reset both poly and two line mode rulers details
  resetRuler() {
    // Do not wipe all rulers globally; keep per-cell data intact
    this.isDrawingRuler = false;
    this.rulerStart = null;
    this.rulerOnGrid = true;
    this.rulerUnitSymbol = "nm";
    this.rulerLineColor = "white";
    this.rulerValues = 2;
    const { redo } = this.getActiveBuckets();
    if (redo && Array.isArray(redo)) redo.length = 0;
    this.currentPolylinePoints = [];
    this.isDrawingPolyline = false;
    this.currentPolylineId = null;
    this.tempPolylineSegment = null;
    this.isProcessingDoubleClick = false;
  }
  resetDrawingState() {
    this.isDrawingRuler = false;
    this.rulerStart = null;
    this.tempRuler = null;
    this.startMarker = null;

    // Reset polyline drawing state
    this.currentPolylinePoints = [];
    this.isDrawingPolyline = false;
    this.tempPolylineSegment = null;
    this.isProcessingDoubleClick = false;
  }
  resetCurrentCellRulers() {
    const currentCell = this.getCurrentCell();
    if (!currentCell) {
      console.warn("No active cell to reset rulers for");
      return false;
    }

    // Clear rulers for current cell
    if (this.rulersByCell[currentCell]) {
      this.rulersByCell[currentCell] = [];
    }

    // Clear redo stack for current cell if it belongs to this cell
    if (this._redoCell === currentCell) {
      this.redoStack = [];
    }

    // Reset drawing state
    this.resetDrawingState();

    console.log(`Reset rulers for cell: ${currentCell}`);
    return true;
  }
  // reset polyline details
  resetPolyline() {
    this.currentPolylinePoints = [];
    this.isDrawingPolyline = false;
    this.tempPolylineSegment = null;
    this.startMarker = null;
  }
  // reset two pointer rulers details
  resetStandardRuler() {
    this.isDrawingRuler = false;
    this.rulerStart = null;
    this.tempRuler = null;
    this.startMarker = null;
    const { redo } = this.getActiveBuckets();
    if (redo && Array.isArray(redo)) redo.length = 0;
  }
  // Helper method to apply angle constraints
  applyAngleConstraints(startX, startY, endX, endY) {
    const dx = endX - startX;
    const dy = endY - startY;
    const length = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx);

    let snappedX = endX;
    let snappedY = endY;

    if (this.rulerLineMode === "diagonal") {
      const snapAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
      snappedX = startX + length * Math.cos(snapAngle);
      snappedY = startY + length * Math.sin(snapAngle);
    } else if (this.rulerLineMode === "orthogonal") {
      const snapAngle = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
      snappedX = startX + length * Math.cos(snapAngle);
      snappedY = startY + length * Math.sin(snapAngle);
    } else if (["5", "10", "15"].includes(this.rulerLineMode)) {
      const deg = parseInt(this.rulerLineMode);
      const snapRad = (deg * Math.PI) / 180;
      const snapAngle = Math.round(angle / snapRad) * snapRad;
      snappedX = startX + length * Math.cos(snapAngle);
      snappedY = startY + length * Math.sin(snapAngle);
    }

    // Apply final grid snap to angle-constrained positions
    if (this.rulerOnGrid && this.rulerLineMode !== "free") {
      const finalSnap = this.snapPointToGrid({ x: snappedX, y: snappedY });
      snappedX = finalSnap.x;
      snappedY = finalSnap.y;
    }

    return { snappedX, snappedY };
  }

  // Handle mouse move global ruler logic
  handleRulerMouseMove(e) {
    if (this.polylineMode && this.isDrawingPolyline) {
      return this.handlePolylineMouseMove(e);
    } else if (!this.polylineMode && this.isDrawingRuler && this.rulerStart) {
      return this.handleStandardRulerMouseMove(e);
    }
  }

  // handle mouse for two point line
  handleStandardRulerMouseMove(e) {
    let { x, y } = this.getWorldCoords(e);

    if (this.rulerOnGrid) {
      const snappedMouse = this.snapPointToGrid({ x, y });
      x = snappedMouse.x;
      y = snappedMouse.y;
    }

    const { snappedX, snappedY } = this.applyAngleConstraints(
      this.rulerStart.x,
      this.rulerStart.y,
      x,
      y
    );

    this.tempRuler = {
      start: this.rulerStart,
      end: { x: snappedX, y: snappedY },
      offset: { x: 0, y: 0 },
      unit: this.rulerUnitSymbol,
      mode: this.rulerLineMode,
    };
  }
  //handle mouse for poly line mode
  handlePolylineMouseMove(e) {
    if (this.currentPolylinePoints.length === 0) return;

    let { x, y } = this.getWorldCoords(e);

    if (this.rulerOnGrid) {
      const snappedMouse = this.snapPointToGrid({ x, y });
      x = snappedMouse.x;
      y = snappedMouse.y;
    }

    const lastPoint =
      this.currentPolylinePoints[this.currentPolylinePoints.length - 1];
    const { snappedX, snappedY } = this.applyAngleConstraints(
      lastPoint.x,
      lastPoint.y,
      x,
      y
    );

    // Calculate cumulative distance up to current mouse position
    let cumulativeDistance = 0;
    // Add all existing segment distances
    for (let i = 1; i < this.currentPolylinePoints.length; i++) {
      const prev = this.currentPolylinePoints[i - 1];
      const curr = this.currentPolylinePoints[i];
      cumulativeDistance += Math.hypot(curr.x - prev.x, curr.y - prev.y);
    }
    // Add current segment distance
    cumulativeDistance += Math.hypot(
      snappedX - lastPoint.x,
      snappedY - lastPoint.y
    );

    this.tempPolylineSegment = {
      start: lastPoint,
      end: { x: snappedX, y: snappedY },
      offset: { x: 0, y: 0 },
      unit: this.rulerUnitSymbol,
      mode: this.rulerLineMode,
      cumulativeDistance: cumulativeDistance, // Real-time cumulative distance
    };
  }

  drawRulers() {
    const ctx = this.engine.ctx;
    ctx.save();
    ctx.translate(this.engine.offsetX, this.engine.offsetY);
    ctx.scale(this.engine.scale, this.engine.scale);

    // Draw all existing rulers
    const { list } = this.getActiveBuckets();
    for (let ruler of list) {
      if (ruler.polylineMode && ruler.points) {
        // Draw polyline ruler
        this.drawPolylineRuler(ruler);
      } else {
        // Draw standard ruler
        const start = {
          x: ruler.start.x + ruler.offset.x,
          y: ruler.start.y + ruler.offset.y,
        };

        const end = {
          x: ruler.end.x + ruler.offset.x,
          y: ruler.end.y + ruler.offset.y,
        };

        this.drawPlusIcon(start.x, start.y);
        this.drawPlusIcon(end.x, end.y);
        this.drawLine(start, end);
        this.drawTicks(start, end);
      }
    }

    // Draw start marker for current drawing
    if (this.startMarker && (this.isDrawingRuler || this.isDrawingPolyline)) {
      this.drawPlusIcon(this.startMarker.x, this.startMarker.y);
    }

    // Draw all intermediate points and segments for current polyline
    if (this.isDrawingPolyline) {
      // Calculate cumulative distances for all existing segments
      let runningTotal = 0;

      for (let i = 1; i < this.currentPolylinePoints.length; i++) {
        const start = this.currentPolylinePoints[i - 1];
        const end = this.currentPolylinePoints[i];
        const segmentDistance = Math.hypot(end.x - start.x, end.y - start.y);
        runningTotal += segmentDistance;

        this.drawPlusIcon(end.x, end.y);
        this.drawLine(start, end);

        // Show cumulative distance for this segment
        this.drawPolylineSegmentTicks(start, end, runningTotal);
      }
    }

    // Draw temporary ruler (standard mode)
    if (this.tempRuler && this.isDrawingRuler && !this.polylineMode) {
      ctx.save();
      ctx.globalAlpha = 0.7;
      this.drawTempRuler(this.tempRuler);
      ctx.restore();
    }

    // Draw temporary polyline segment with real-time cumulative distance
    if (this.tempPolylineSegment && this.isDrawingPolyline) {
      ctx.save();
      ctx.globalAlpha = 0.7;
      const tempStart = {
        x: this.tempPolylineSegment.start.x + this.tempPolylineSegment.offset.x,
        y: this.tempPolylineSegment.start.y + this.tempPolylineSegment.offset.y,
      };

      const tempEnd = {
        x: this.tempPolylineSegment.end.x + this.tempPolylineSegment.offset.x,
        y: this.tempPolylineSegment.end.y + this.tempPolylineSegment.offset.y,
      };

      this.drawPlusIcon(tempStart.x, tempStart.y);
      this.drawPlusIcon(tempEnd.x, tempEnd.y);
      this.drawLine(tempStart, tempEnd);

      // Show real-time cumulative distance at the end point
      if (this.tempPolylineSegment.cumulativeDistance) {
        this.drawCumulativeEndTicks(
          tempStart,
          tempEnd,
          this.tempPolylineSegment.cumulativeDistance
        );
      } else {
        this.drawTicks(tempStart, tempEnd);
      }
      ctx.restore();
    }

    ctx.restore();
  }

  drawTempRuler(ruler) {
    const tempStart = {
      x: ruler.start.x + ruler.offset.x,
      y: ruler.start.y + ruler.offset.y,
    };

    const tempEnd = {
      x: ruler.end.x + ruler.offset.x,
      y: ruler.end.y + ruler.offset.y,
    };

    this.drawPlusIcon(tempStart.x, tempStart.y);
    this.drawPlusIcon(tempEnd.x, tempEnd.y);
    this.drawLine(tempStart, tempEnd);
    this.drawTicks(tempStart, tempEnd);
  }

  // New method to draw polyline rulers
  drawPolylineRuler(ruler) {
    if (!ruler.points || ruler.points.length < 2) return;

    let runningTotal = 0;

    // Draw all segments of the polyline
    for (let i = 1; i < ruler.points.length; i++) {
      const start = {
        x: ruler.points[i - 1].x + ruler.offset.x,
        y: ruler.points[i - 1].y + ruler.offset.y,
      };
      const end = {
        x: ruler.points[i].x + ruler.offset.x,
        y: ruler.points[i].y + ruler.offset.y,
      };

      // Calculate running total up to this point
      const segmentDistance = Math.hypot(
        ruler.points[i].x - ruler.points[i - 1].x,
        ruler.points[i].y - ruler.points[i - 1].y
      );
      runningTotal += segmentDistance;

      // Draw plus icons for each point
      if (i === 1) this.drawPlusIcon(start.x, start.y); // First point
      this.drawPlusIcon(end.x, end.y);

      // Draw line segment
      this.drawLine(start, end);

      // Show cumulative distance for this segment
      this.drawPolylineSegmentTicks(start, end, runningTotal);
    }
  }

  // New method to show cumulative distance for polyline segments
  drawPolylineSegmentTicks(start, end, cumulativeDistance) {
    const ctx = this.engine.ctx;
    const unitSymbol = this.rulerUnitSymbol || "nm";
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const remToPx = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const tickLength = (0.7 * remToPx) / this.engine.scale;
    let baseLabelOffset =
      (0.2 * remToPx) / this.engine.scale + 20 / this.engine.scale;

    const isVertical = Math.abs(Math.sin(angle)) > 0.8;
    if (isVertical) {
      baseLabelOffset = 3 * baseLabelOffset;
    }

    const scaledFontSize = (this.fontSize * remToPx) / this.engine.scale;
    ctx.font = `${scaledFontSize}px Arial`;
    ctx.strokeStyle = this.rulerLineColor || "white";
    ctx.fillStyle = "white";
    ctx.textBaseline = "middle";

    // Draw tick at the END point showing cumulative distance
    const tx1 = end.x + Math.cos(angle + Math.PI / 2) * tickLength;
    const ty1 = end.y + Math.sin(angle + Math.PI / 2) * tickLength;
    const tx2 = end.x + Math.cos(angle - Math.PI / 2) * tickLength;
    const ty2 = end.y + Math.sin(angle - Math.PI / 2) * tickLength;

    ctx.beginPath();
    ctx.moveTo(tx1, ty1);
    ctx.lineTo(tx2, ty2);
    ctx.stroke();

    // Convert cumulative distance to units
    this.unitScaleFactor = this.UNIT_CONVERSIONS[this.rulerUnitSymbol];
    const value = cumulativeDistance * this.unitScaleFactor;

    const labelX =
      end.x + Math.cos(angle + Math.PI / 2) * (tickLength + baseLabelOffset);
    const labelY =
      end.y + Math.sin(angle + Math.PI / 2) * (tickLength + baseLabelOffset);

    // Use fixed precision from constructor
    const precision = this.UNIT_PRECISION[unitSymbol] || 1;

    // Show cumulative distance at each point
    ctx.fillText(`${value.toFixed(precision)} ${unitSymbol}`, labelX, labelY);
  }
  // Method to show cumulative distance at the end point only (for temp segments)
  drawCumulativeEndTicks(start, end, cumulativeDistance) {
    const ctx = this.engine.ctx;
    const unitSymbol = this.rulerUnitSymbol || "nm";
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const remToPx = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const tickLength = (0.7 * remToPx) / this.engine.scale;
    let baseLabelOffset =
      (0.2 * remToPx) / this.engine.scale + 20 / this.engine.scale;

    const isVertical = Math.abs(Math.sin(angle)) > 0.8;
    if (isVertical) {
      baseLabelOffset = 3 * baseLabelOffset;
    }

    const scaledFontSize = (this.fontSize * remToPx) / this.engine.scale;
    ctx.font = `${scaledFontSize}px Arial`;
    ctx.strokeStyle = this.rulerLineColor || "white";
    ctx.fillStyle = "white";
    ctx.textBaseline = "middle";

    // Draw tick at the END point showing cumulative distance
    const tx1 = end.x + Math.cos(angle + Math.PI / 2) * tickLength;
    const ty1 = end.y + Math.sin(angle + Math.PI / 2) * tickLength;
    const tx2 = end.x + Math.cos(angle - Math.PI / 2) * tickLength;
    const ty2 = end.y + Math.sin(angle - Math.PI / 2) * tickLength;

    ctx.beginPath();
    ctx.moveTo(tx1, ty1);
    ctx.lineTo(tx2, ty2);
    ctx.stroke();

    // Convert cumulative distance to units
    this.unitScaleFactor = this.UNIT_CONVERSIONS[this.rulerUnitSymbol];
    const value = cumulativeDistance * this.unitScaleFactor;

    const labelX =
      end.x + Math.cos(angle + Math.PI / 2) * (tickLength + baseLabelOffset);
    const labelY =
      end.y + Math.sin(angle + Math.PI / 2) * (tickLength + baseLabelOffset);

    // Use fixed precision from constructor
    const precision = this.UNIT_PRECISION[unitSymbol] || 1;

    // Add "Total:" prefix for cumulative distance at end
    ctx.fillText(
      `Total: ${value.toFixed(precision)} ${unitSymbol}`,
      labelX,
      labelY
    );
  }

  // Existing drawPlusIcon and drawLine methods remain the same
  drawPlusIcon(x, y) {
    const ctx = this.engine.ctx;
    const size = 8 / this.engine.scale;
    ctx.save();
    ctx.strokeStyle = "yellow";
    ctx.lineWidth = 1 / this.engine.scale;
    ctx.beginPath();
    ctx.moveTo(x - size, y);
    ctx.lineTo(x + size, y);
    ctx.moveTo(x, y - size);
    ctx.lineTo(x, y + size);
    ctx.stroke();
    ctx.restore();
  }

  drawLine(start, end) {
    const ctx = this.engine.ctx;
    ctx.strokeStyle = this.rulerLineColor || "white";
    ctx.lineWidth = 1.3 / this.engine.scale;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
  }

  drawTicks(start, end) {
    const ctx = this.engine.ctx;
    const tickCount = this.rulerValues || 1;
    const unitSymbol = this.rulerUnitSymbol || "nm";
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const totalPixelLength = Math.hypot(end.x - start.x, end.y - start.y);
    const spacing = totalPixelLength / (tickCount - 1);
    const remToPx = parseFloat(
      getComputedStyle(document.documentElement).fontSize
    );
    const tickLength = (0.7 * remToPx) / this.engine.scale;
    let baseLabelOffset =
      (0.2 * remToPx) / this.engine.scale + 20 / this.engine.scale;

    const isVertical = Math.abs(Math.sin(angle)) > 0.8;
    if (isVertical) {
      baseLabelOffset = 3 * baseLabelOffset;
    }

    const scaledFontSize = (this.fontSize * remToPx) / this.engine.scale;
    ctx.font = `${scaledFontSize}px Arial`;
    ctx.strokeStyle = this.rulerLineColor || "white";
    ctx.fillStyle = "white";
    ctx.textBaseline = "middle";

    for (let i = 0; i < tickCount; i++) {
      const dist = spacing * i;
      const x = start.x + Math.cos(angle) * dist;
      const y = start.y + Math.sin(angle) * dist;

      const tx1 = x + Math.cos(angle + Math.PI / 2) * tickLength;
      const ty1 = y + Math.sin(angle + Math.PI / 2) * tickLength;
      const tx2 = x + Math.cos(angle - Math.PI / 2) * tickLength;
      const ty2 = y + Math.sin(angle - Math.PI / 2) * tickLength;

      ctx.beginPath();
      ctx.moveTo(tx1, ty1);
      ctx.lineTo(tx2, ty2);
      ctx.stroke();

      this.unitScaleFactor = this.UNIT_CONVERSIONS[this.rulerUnitSymbol];
      const value = dist * this.unitScaleFactor;

      const labelX =
        x + Math.cos(angle + Math.PI / 2) * (tickLength + baseLabelOffset);
      const labelY =
        y + Math.sin(angle + Math.PI / 2) * (tickLength + baseLabelOffset);

      // Use fixed precision based on unit type
      const precision = this.UNIT_PRECISION[unitSymbol] || 1;
      ctx.fillText(`${value.toFixed(precision)} ${unitSymbol}`, labelX, labelY);
    }
  }

  // Enhanced undo functionality
  undoRuler() {
    const { list, redo } = this.getActiveBuckets();

    if (!list || list.length === 0) return;

    // If currently drawing polyline, remove last point instead of whole ruler
    if (this.isDrawingPolyline && this.currentPolylinePoints.length > 1) {
      this.currentPolylinePoints.pop();
      return;
    }

    const removedRuler = list.pop();
    redo.push(removedRuler);
  }
  // enhanced redo functionality
  redoRuler() {
    const { list, redo } = this.getActiveBuckets();
    if (!redo || redo.length === 0) return;
    const restoredRuler = redo.pop();
    list.push(restoredRuler);
  }

  checkRulerArrays() {
    console.log(this.rulersByCell, this.redoStack);
  }
  onToolchange() {
    this.tempRuler = null;
    this.startMarker = null;
    this.isDrawingRuler = false;
    this.isDrawingPolyline = false;
  }
}
